import { useQuery } from "@tanstack/react-query";

import { useGetAllGroups } from "@synthesis/shared-services/auth/group/hooks";
import { getUserGroups } from "@synthesis/shared-services/auth/group/client";
import { useGetUsersByGroup } from "@synthesis/shared-services/auth/group/hooks";
import type { GroupDto } from "@synthesis/shared-services/auth/group/types";
import { getUserInfo } from "@synthesis/shared-services/auth/user/client";
import type { UserDto } from "@synthesis/shared-services/auth/user/types";
import { useGetProjectMeta } from "@synthesis/shared-services/project-gge/projectMetaController/hooks";
import { useGetProjectUsers } from "@synthesis/shared-services/project-gge/projectController/hooks";
import type { AssignedUserDto } from "@synthesis/shared-services/project-gge/projectController/types";
import type { ProjectMetaDto } from "@synthesis/shared-services/project-gge/projectMetaController/types";
import { logger } from "@synthesis/shared-utils";

import type { UserDataType } from "../list-of-experts-page.lib";

type AssignedUserWithRole = AssignedUserDto & {
  projectRole?: { id?: string; name?: string } | null;
};

type GroupsAndMeta = {
  groups: GroupDto[];
  projectMeta: ProjectMetaDto[] | null;
};

const fetchUserData = async (assignedUsers: AssignedUserWithRole[]) => {
  if (!assignedUsers || assignedUsers.length === 0) return [];

  const userDataPromises = assignedUsers.map(async (user) => {
    try {
      const [responseGroupData, responseData] = await Promise.all([
        getUserGroups({ path: { userId: user.userId ?? "" } }),
        getUserInfo({ path: { userId: user.userId ?? "" } }),
      ]);

      return {
        groups: responseGroupData.data as GroupDto[],
        user: responseData.data as UserDto,
        projectRole: user.projectRole ?? null,
      };
    } catch (error) {
      logger.error(`Error fetching user data for user ID ${user.userId}:`, error);
      return {
        groups: [] as GroupDto[],
        user: { id: user.userId ?? "" } as UserDto,
        projectRole: user.projectRole ?? null,
      };
    }
  });

  return Promise.all(userDataPromises);
};

export const useGroupsQuery = (
  projectId?: string,
): {
  isGroupsLoading: boolean;
  selectValues: { value: string; label: string }[];
  allGroupsMap: Map<string, GroupDto>;
  groups: GroupDto[];
  initialGroups: GroupDto[];
} => {
  const { data: groups = [], isLoading: isGroupsLoading } = useGetAllGroups();

  const { data: projectMeta = null, isLoading: isMetaLoading } = useGetProjectMeta(
    { path: { projectId: projectId ?? "" } },
    {
      query: { enabled: !!projectId },
    },
  );

  const queryData: GroupsAndMeta = { groups, projectMeta };

  const allGroupsMap = new Map(queryData.groups.filter((group) => group.id).map((group) => [group.id!, group]));

  const selectValues: { value: string; label: string }[] = [];
  queryData.groups.forEach((value) => {
    if (value.id && value.name) {
      selectValues.push({ value: value.id, label: value.name });
    }
  });

  const ownerGroup = queryData.projectMeta?.find((meta) => meta.key === "ownerGroup");
  const initialGroups = ownerGroup
    ? (JSON.parse(ownerGroup.value) as string[]).map((groupId) => allGroupsMap.get(groupId)).filter((group): group is GroupDto => !!group)
    : [];

  return {
    isGroupsLoading: isGroupsLoading || isMetaLoading,
    selectValues,
    allGroupsMap,
    groups: queryData.groups,
    initialGroups,
  };
};

export const useUsersByGroupQuery = (groupId: string | null) => {
  const { data: usersByGroup = [], isLoading: isMembersLoading } = useGetUsersByGroup(
    { path: { groupId: groupId ?? "" } },
    {
      query: { enabled: !!groupId },
    },
  );

  return [isMembersLoading, usersByGroup] as const;
};

export const useAssignedUsersQueryData = (
  projectId: string | undefined,
): {
  assignedUsersData: UserDataType[];
  isAssignedUsersDataLoading: boolean;
} => {
  const {
    data: roleCache,
    isLoading: isAssignedUsersLoading,
    isSuccess: isAssignedUsersSuccess,
  } = useGetProjectUsers(
    { path: { projectId: projectId ?? "" } },
    {
      query: { enabled: !!projectId },
    },
  );

  const assignedUsers = roleCache?.assignedUsers ?? [];

  const { data: userData = [], isLoading: isUserDataLoading } = useQuery({
    queryKey: ["project-users-data", projectId, assignedUsers],
    queryFn: () => fetchUserData(assignedUsers),
    enabled: isAssignedUsersSuccess && !!assignedUsers.length,
  });

  return {
    assignedUsersData: userData,
    isAssignedUsersDataLoading: isAssignedUsersLoading || isUserDataLoading,
  };
};
